import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface SimpleDateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

export function SimpleDateRangePicker({
  value,
  onChange,
  disabled = false,
  className,
  placeholder = "Select date range"
}: SimpleDateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const formatDateRange = () => {
    if (value.from && value.to) {
      return `${format(value.from, 'MMM dd, yyyy')} - ${format(value.to, 'MMM dd, yyyy')}`;
    }
    if (value.from) {
      return format(value.from, 'MMM dd, yyyy');
    }
    return placeholder;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value.from && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDateRange()}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-auto p-0 bg-white shadow-lg border border-gray-200" 
        align="start"
        sideOffset={4}
      >
        <Calendar
          mode="range"
          selected={value}
          onSelect={(range) => {
            if (range) {
              onChange({ from: range.from, to: range.to });
              // Close popover with smooth animation when both dates are selected
              if (range.from && range.to) {
                // Small delay to show the selection before closing
                setTimeout(() => setIsOpen(false), 150);
              }
            }
          }}
          numberOfMonths={2}
          className={cn(
            "rounded-md bg-white",
            // Enhanced styling for selected dates
            "[&_.rdp-day_selected]:bg-blue-500 [&_.rdp-day_selected]:text-white [&_.rdp-day_selected]:font-medium",
            "[&_.rdp-day_selected:hover]:bg-primary",
            // Range styling
            "[&_.rdp-day_range_start]:bg-blue-500 [&_.rdp-day_range_start]:text-white",
            "[&_.rdp-day_range_end]:bg-blue-500 [&_.rdp-day_range_end]:text-white", 
            "[&_.rdp-day_range_middle]:bg-blue-100 [&_.rdp-day_range_middle]:text-blue-900",
            // Hover effects
            "[&_.rdp-day:hover]:bg-blue-50 [&_.rdp-day:hover]:text-blue-900",
            // Today styling
            "[&_.rdp-day_today]:bg-gray-100 [&_.rdp-day_today]:font-medium",
            // General day styling
            "[&_.rdp-day]:rounded-md [&_.rdp-day]:transition-colors [&_.rdp-day]:duration-200"
          )}
        />
      </PopoverContent>
    </Popover>
  );
}
